import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AcademyService } from '../services/academy-service';
import { toast } from 'sonner';
import type { 
  Course, 
  CourseWithId, 
  Enrollment, 
  EnrollmentWithId,
} from '@/shared/schema';

export const useAcademy = {
  // --- COURSES ---
  
  /**
   * Obtiene todos los cursos de un negocio
   */
  useCourses: (ownerId?: string) => {
    return useQuery({
      queryKey: ['courses', ownerId],
      queryFn: () => (ownerId ? AcademyService.getCourses(ownerId) : []),
      enabled: !!ownerId,
    });
  },

  /**
   * Obtiene un curso específico
   */
  useCourse: (ownerId?: string, courseId?: string) => {
    return useQuery({
      queryKey: ['course', ownerId, courseId],
      queryFn: () => (ownerId && courseId ? AcademyService.getCourseById(ownerId, courseId) : null),
      enabled: !!ownerId && !!courseId,
    });
  },

  /**
   * Crea un nuevo curso
   */
  useCreateCourse: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ ownerId, data }: { ownerId: string; data: Course }) => 
        AcademyService.createCourse(ownerId, data),
      onSuccess: (_, { ownerId }) => {
        queryClient.invalidateQueries({ queryKey: ['courses', ownerId] });
        toast.success('Curso creado exitosamente');
      },
      onError: (err: any) => {
        console.error('Error creating course:', err);
        toast.error('Error al crear el curso');
      }
    });
  },

  /**
   * Actualiza un curso
   */
  useUpdateCourse: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ ownerId, courseId, data }: { ownerId: string; courseId: string; data: Partial<Course> }) => 
        AcademyService.updateCourse(ownerId, courseId, data),
      onSuccess: (_, { ownerId, courseId }) => {
        queryClient.invalidateQueries({ queryKey: ['courses', ownerId] });
        queryClient.invalidateQueries({ queryKey: ['course', ownerId, courseId] });
        toast.success('Curso actualizado');
      },
      onError: (err: any) => {
        console.error('Error updating course:', err);
        toast.error('Error al actualizar el curso');
      }
    });
  },

  /**
   * Elimina un curso (Soft Delete)
   */
  useDeleteCourse: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ ownerId, courseId }: { ownerId: string; courseId: string }) => 
        AcademyService.deleteCourse(ownerId, courseId),
      onSuccess: (_, { ownerId }) => {
        queryClient.invalidateQueries({ queryKey: ['courses', ownerId] });
        toast.success('Curso eliminado');
      },
      onError: (err: any) => {
        console.error('Error deleting course:', err);
        toast.error('Error al eliminar el curso');
      }
    });
  },

  // --- ENROLLMENTS / INVITATIONS ---

  /**
   * Obtiene todas las inscripciones del negocio (Invitaciones Pendientes)
   */
  useAllEnrollments: (ownerId?: string) => {
    return useQuery({
      queryKey: ['enrollments', ownerId],
      queryFn: () => (ownerId ? AcademyService.getAllEnrollments(ownerId) : []),
      enabled: !!ownerId,
    });
  },

  /**
   * Obtiene las inscripciones de un curso específico
   */
  useEnrollments: (ownerId?: string, courseId?: string) => {
    return useQuery({
      queryKey: ['enrollments', ownerId, courseId],
      queryFn: () => (ownerId && courseId ? AcademyService.getEnrollmentsByCourse(ownerId, courseId) : []),
      enabled: !!ownerId && !!courseId,
    });
  },

  /**
   * Crea una invitación manual (Genera Token)
   */
  useCreateEnrollment: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ ownerId, data }: { ownerId: string; data: Omit<Enrollment, 'audit'> }) => 
        AcademyService.createEnrollment(ownerId, data),
      onSuccess: (_, { ownerId, data }) => {
        queryClient.invalidateQueries({ queryKey: ['enrollments', ownerId] });
        queryClient.invalidateQueries({ queryKey: ['clients', ownerId] });
        if (data.clientId) {
          queryClient.invalidateQueries({ queryKey: ['client', data.clientId] });
        }
        toast.success('Invitación creada exitosamente.');
      },
    });
  },

  // --- MASTER STUDENTS (COMMUNITY) ---

  /**
   * Obtiene todos los alumnos de la comunidad (Fichas Maestras)
   */
  useStudents: (ownerId?: string) => {
    return useQuery({
      queryKey: ['academy-students', ownerId],
      queryFn: () => (ownerId ? AcademyService.getStudents(ownerId) : []),
      enabled: !!ownerId,
    });
  },

  /**
   * Obtiene un alumno específico por su UID
   */
  useStudent: (ownerId?: string, studentUid?: string) => {
    return useQuery({
      queryKey: ['academy-student', ownerId, studentUid],
      queryFn: () => (ownerId && studentUid ? AcademyService.getStudentByUid(ownerId, studentUid) : null),
      enabled: !!ownerId && !!studentUid,
    });
  },

  /**
   * Actualiza permisos de curso del alumno
   */
  useUpdateStudentCourses: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ ownerId, studentUid, courses, clientId }: { ownerId: string; studentUid: string | null; courses: string[]; clientId?: string }) => 
        AcademyService.updateStudentCourses(ownerId, studentUid, courses, clientId),
      onSuccess: (_, { ownerId, studentUid, clientId }) => {
        queryClient.invalidateQueries({ queryKey: ['academy-students', ownerId] });
        queryClient.invalidateQueries({ queryKey: ['academy-student', ownerId, studentUid] });
        queryClient.invalidateQueries({ queryKey: ['enrollments', ownerId] });
        // Invalidate both possible client queries
        queryClient.invalidateQueries({ queryKey: ['clients', ownerId] });
        if (clientId) {
          queryClient.invalidateQueries({ queryKey: ['client', clientId] });
        }
        toast.success('Accesos actualizados');
      },
    });
  }
};
